import { create } from 'zustand';

import type { ActiveSession } from '@/lib/api/contacts.api';

interface ActiveContactsState {
  /** Set of contact IDs that currently have a live session (voice or text). */
  activeIds: Set<string>;
  /** Subset of activeIds whose live session is inbound (contact reached out). */
  inboundIds: Set<string>;
  /** Live inbound sessions with their channel, for channel-aware auto-open. */
  inboundSessions: ActiveSession[];
  /** Replace the entire active set (initial fetch + refetches). */
  setActiveSessions: (sessions: ActiveSession[]) => void;
  /** Mark a single contact as active (WS event — direction unknown). */
  addActive: (id: string) => void;
  /** Mark a single contact as inactive (WS event). */
  removeActive: (id: string) => void;
}

export const useActiveContactsStore = create<ActiveContactsState>((set) => ({
  activeIds: new Set<string>(),
  inboundIds: new Set<string>(),
  inboundSessions: [],
  setActiveSessions: (sessions) => {
    const inbound = sessions.filter((s) => s.direction === 'INBOUND');
    return set({
      activeIds: new Set(sessions.map((s) => s.id)),
      inboundIds: new Set(inbound.map((s) => s.id)),
      inboundSessions: inbound,
    });
  },
  addActive: (id) =>
    set((state) => {
      if (state.activeIds.has(id)) return state;
      const next = new Set(state.activeIds);
      next.add(id);
      return { activeIds: next };
    }),
  removeActive: (id) =>
    set((state) => {
      if (!state.activeIds.has(id) && !state.inboundIds.has(id)) return state;
      const nextActive = new Set(state.activeIds);
      nextActive.delete(id);
      const nextInbound = new Set(state.inboundIds);
      nextInbound.delete(id);
      return {
        activeIds: nextActive,
        inboundIds: nextInbound,
        inboundSessions: state.inboundSessions.filter((s) => s.id !== id),
      };
    }),
}));
